import { Box, Center, Flex, HStack, Input, Spinner, Stack, Text } from '@chakra-ui/react';
import { ChangeEvent, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { Trans } from '@lingui/react/macro';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { skipToken } from '@tanstack/react-query';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import {
  CourseKindLabels,
  courseSection,
} from '@duke-hq/app-provider-web/tenant/Courses/constants';
import { ResourceStatusBadge } from '@duke-hq/ui/components/ResourceStatusBadge/ResourceStatusBadge';
import { SectionedList } from '@duke-hq/ui/components/SectionedList/SectionedList';

type Course = RouterOutputs['courses']['all'][number];

export function CourseSelection({
  course,
  student,
  onCourse,
}: {
  course: { id: string; title: string } | undefined;
  student: { id: string } | undefined;
  onCourse: (course: Course) => void;
}) {
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch] = useDebounce(search, 300);

  const { data: courses, isLoading } = trpcTenant.courses.all.useQuery({ order: 'category' });
  const { data: studentEnrolments, isLoading: isStudentEnrolmentsLoading } =
    trpcTenant.enrolments.getEnrolledCourseStatuses.useQuery(
      typeof student?.id === 'string' ? { studentId: student.id } : skipToken,
      { enabled: typeof student?.id === 'string' },
    );

  const courseOptions = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase();
    return (courses ?? [])
      .filter(
        (option) =>
          query === '' ||
          option.title.toLowerCase().includes(query) ||
          (option.identifier ?? '').toLowerCase().includes(query),
      )
      .map((option) => {
        const enrolment = studentEnrolments?.find((e) => e.course_id === option.id);
        return {
          course: option,
          enrolmentStatus: enrolment?.status,
          isDisabled: enrolment != null,
        };
      });
  }, [courses, studentEnrolments, debouncedSearch]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  if (isLoading || (typeof student?.id === 'string' && isStudentEnrolmentsLoading)) {
    return (
      <Center>
        <Spinner />
      </Center>
    );
  }

  return (
    <Stack spacing={3} w="full">
      <Input
        autoComplete="off"
        autoFocus
        fontSize="sm"
        minH="8"
        maxH="8"
        onChange={handleInputChange}
        placeholder="Search"
        size="sm"
        type="text"
        value={search}
        width="full"
      />
      <SectionedList
        items={courseOptions}
        getItemKey={(option) => option.course.id}
        getSection={(option) => courseSection(option.course)}
        isItemSelected={(option) => option.course.id === course?.id}
        isItemDisabled={(option) => option.isDisabled}
        onSelectItem={(option) => onCourse(option.course)}
        emptyState={
          <Text color="fg.muted" textAlign="left" textStyle="sm">
            <Trans>No courses found.</Trans>
          </Text>
        }
        renderItem={({ course: option, enrolmentStatus }) => (
          <Flex align="center" justify="space-between" w="full">
            <Box>
              <HStack justify="start">
                <Text color="fg.subtle" fontSize="sm" lineHeight="5">
                  {option.identifier}
                </Text>
                {typeof option.specialisation === 'string' && option.specialisation !== '' ? (
                  <Text color="fg.emphasis" fontSize="sm" lineHeight="5" fontWeight="bold">
                    {option.title}
                  </Text>
                ) : null}
              </HStack>
              {typeof option.specialisation === 'string' && option.specialisation !== '' ? (
                <Text color="fg.subtle" fontSize="xs" lineHeight="5">
                  {option.specialisation}
                </Text>
              ) : (
                <Text color="fg.emphasis" fontSize="sm" lineHeight="5" fontWeight="bold">
                  <Box as="span">
                    {CourseKindLabels[option.kind] != null ? (
                      <Box as="span">{CourseKindLabels[option.kind]}: </Box>
                    ) : null}
                    <Box as="span">{option.title}</Box>
                  </Box>
                </Text>
              )}
            </Box>
            {enrolmentStatus ? (
              <Flex align="flex-end">
                <ResourceStatusBadge status={enrolmentStatus} />
              </Flex>
            ) : null}
          </Flex>
        )}
      />
    </Stack>
  );
}
